import { useEffect, useRef, useState } from 'react';
import type { Audience } from '@epasu/core';
import { Plus, Trash2, UserPlus } from 'lucide-react';
import { crm } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';

interface Props {
  audiences: Audience[];
  onClose: () => void;
  onDone: (customerId: string) => void;
}

interface Person {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  formOfAddress: '' | 'herr' | 'frau';
}

const LEERE_PERSON: Person = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  role: '',
  formOfAddress: '',
};

const istLeer = (p: Person) =>
  !p.firstName.trim() && !p.lastName.trim() && !p.email.trim() && !p.phone.trim();

/**
 * Einen Kunden von Grund auf anlegen: Firma, Ansprechpartner, Kundenstatus.
 *
 * Der Weg für den, der nicht erst als Lead durch die Kampagnen lief — ein
 * Kunde per Empfehlung oder Anruf. Der Dialog legt Firma und Personen als
 * gewöhnliche Firma und Kontakte an (Lifecycle „kontakt“) und befördert die
 * Firma dann; der erste Ansprechpartner wird Hauptansprechpartner.
 *
 * Die Schritte laufen nacheinander. Schlägt einer fehl (etwa eine E-Mail, die
 * es schon gibt), merkt sich der Dialog, was schon angelegt ist, und legt beim
 * nächsten Versuch nichts doppelt an.
 */
export function NewCustomerDialog({ audiences, onClose, onDone }: Props) {
  const [audienceId, setAudienceId] = useState(audiences[0]?.id ?? '');
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [website, setWebsite] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [street, setStreet] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [notes, setNotes] = useState('');
  const [personen, setPersonen] = useState<Person[]>([{ ...LEERE_PERSON }]);
  const [seit, setSeit] = useState(new Date().toISOString().slice(0, 10));
  const [projektTitel, setProjektTitel] = useState('');
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  // Was ein früherer, halb gelungener Versuch schon angelegt hat.
  const angelegtFirma = useRef<string | null>(null);
  const angelegtePersonen = useRef<Map<number, string>>(new Map());
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => nameRef.current?.focus(), []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const setPerson = (i: number, patch: Partial<Person>) =>
    setPersonen((ps) => ps.map((p, j) => (j === i ? { ...p, ...patch } : p)));

  const speichern = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setFehler('Bitte den Namen der Firma angeben.');
    if (!audienceId) return setFehler('Bitte eine Zielgruppe wählen.');

    setBusy(true);
    setFehler(null);
    try {
      if (!angelegtFirma.current) {
        const firma = await crm.organizations.create({
          audienceId,
          name,
          domain,
          website,
          email,
          phone,
          street,
          postalCode,
          city,
          notes,
        });
        angelegtFirma.current = firma.id;
      }
      const firmaId = angelegtFirma.current;

      let haupt: string | null = null;
      for (const [i, p] of personen.entries()) {
        if (istLeer(p)) continue;
        let id = angelegtePersonen.current.get(i) ?? null;
        if (!id) {
          const kontakt = await crm.contacts.create({
            audienceId,
            organizationId: firmaId,
            firstName: p.firstName.trim() || null,
            lastName: p.lastName.trim() || null,
            email: p.email.trim() || null,
            phone: p.phone.trim() || null,
            role: p.role.trim() || null,
            formOfAddress: p.formOfAddress || null,
            lifecycle: 'kontakt',
          });
          id = kontakt.id;
          angelegtePersonen.current.set(i, id);
        }
        haupt ??= id;
      }

      const kunde = await crm.customers.promote({
        organizationId: firmaId,
        primaryContactId: haupt,
        since: seit,
        firstProjectTitle: projektTitel.trim() || null,
      });
      onDone(kunde.id);
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Anlegen fehlgeschlagen');
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Neuer Kunde"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <form
        onSubmit={(e) => void speichern(e)}
        className="my-auto w-full max-w-2xl space-y-6 rounded-lg border bg-background p-6 shadow-lg"
      >
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <UserPlus className="h-5 w-5 text-muted-foreground" />
          Neuer Kunde
        </h2>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold">Firma</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name" className="sm:col-span-2">
              <Input
                ref={nameRef}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Musterschule Köln"
              />
            </Field>
            <Field label="Zielgruppe">
              <Select value={audienceId} onChange={(e) => setAudienceId(e.target.value)}>
                {audiences.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Domain">
              <Input
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="musterschule.de"
              />
            </Field>
            <Field label="Website">
              <Input value={website} onChange={(e) => setWebsite(e.target.value)} />
            </Field>
            <Field label="Telefon">
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </Field>
            <Field label="E-Mail (zentrale Adresse)" className="sm:col-span-2">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <Field label="Straße" className="sm:col-span-2">
              <Input value={street} onChange={(e) => setStreet(e.target.value)} />
            </Field>
            <Field label="PLZ">
              <Input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
            </Field>
            <Field label="Ort">
              <Input value={city} onChange={(e) => setCity(e.target.value)} />
            </Field>
            <Field label="Notiz" className="sm:col-span-2">
              <textarea
                className="min-h-[4rem] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Field>
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold">Ansprechpartner</h3>
          {personen.map((p, i) => (
            <div key={i} className="space-y-3 rounded-md border p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">
                  {i === 0 ? 'Hauptansprechpartner' : `Ansprechpartner ${i + 1}`}
                </span>
                {personen.length > 1 && !angelegtePersonen.current.has(i) && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Ansprechpartner entfernen"
                    onClick={() => setPersonen((ps) => ps.filter((_, j) => j !== i))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Anrede">
                  <Select
                    value={p.formOfAddress}
                    onChange={(e) =>
                      setPerson(i, { formOfAddress: e.target.value as Person['formOfAddress'] })
                    }
                  >
                    <option value="">—</option>
                    <option value="frau">Frau</option>
                    <option value="herr">Herr</option>
                  </Select>
                </Field>
                <Field label="Vorname">
                  <Input
                    value={p.firstName}
                    onChange={(e) => setPerson(i, { firstName: e.target.value })}
                  />
                </Field>
                <Field label="Nachname">
                  <Input
                    value={p.lastName}
                    onChange={(e) => setPerson(i, { lastName: e.target.value })}
                  />
                </Field>
                <Field label="E-Mail" className="sm:col-span-2">
                  <Input
                    type="email"
                    value={p.email}
                    onChange={(e) => setPerson(i, { email: e.target.value })}
                  />
                </Field>
                <Field label="Telefon">
                  <Input value={p.phone} onChange={(e) => setPerson(i, { phone: e.target.value })} />
                </Field>
                <Field label="Rolle" className="sm:col-span-3">
                  <Input
                    value={p.role}
                    onChange={(e) => setPerson(i, { role: e.target.value })}
                    placeholder="Schulleitung, Sekretariat …"
                  />
                </Field>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPersonen((ps) => [...ps, { ...LEERE_PERSON }])}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Weiterer Ansprechpartner
          </Button>
        </section>

        <section className="grid gap-4 sm:grid-cols-2">
          <Field label="Kunde seit">
            <Input type="date" value={seit} onChange={(e) => setSeit(e.target.value)} />
          </Field>
          <Field label="Erstes Projekt (optional)">
            <Input
              value={projektTitel}
              onChange={(e) => setProjektTitel(e.target.value)}
              placeholder="z. B. Abschlussfotos 2027"
            />
          </Field>
        </section>

        {fehler && (
          <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {fehler}
            {angelegtFirma.current && (
              <span className="mt-1 block text-xs">
                Firma und bereits angelegte Personen bleiben erhalten — beim nächsten Versuch
                entsteht nichts doppelt.
              </span>
            )}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? 'Lege an …' : 'Kunde anlegen'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={`block space-y-1.5 ${className ?? ''}`}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
